import { FolderOpen, History, RefreshCw, Square } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import type { TestingAccess, TestingRunActions, TestingRunVm } from '../model/types'
import { Share, StateBadge, TestingGate } from './TestingParts'

/**
 * Разбор прогона: каждая дорожка отдельно и после слияния, товары — худшие
 * первыми, срезы по пометкам кадров, путаницы, время и чем посчитано.
 */
export function TestingRunScreen({ access, vm, actions }: { access: TestingAccess; vm: TestingRunVm; actions: TestingRunActions }) {
  const { comparison } = vm
  return (
    <TestingGate access={access}>
      {vm.title === null ? (
        <QueryState query={vm.query} emptyText="Прогон не найден" onRetry={actions.reload} />
      ) : (
        <section aria-labelledby="platform-testing-run-title" className="platform-testing">
          <header className="platform-resources__toolbar">
            <h2 id="platform-testing-run-title">{vm.title}</h2>
            <div className="platform-testing__row">
              {vm.active && (
                <Button variant="ghost" icon={<Square size={16} aria-hidden="true" />} disabled={vm.action.busy} onClick={actions.cancel} actionId="platform.testing-run.cancel">
                  Отменить
                </Button>
              )}
              <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-run.reload">
                Обновить
              </Button>
            </div>
          </header>
          <p>
            {vm.state !== null && <StateBadge badge={vm.state} />} · {vm.progress}
          </p>
          {vm.error !== null && <p className="platform-testing__note">Причина: {vm.error}</p>}
          <ActionError error={vm.action.error} onReload={actions.reload} />
          <p className="platform-testing__note">Каталог замера — тестовый: сравнивайте прогоны между собой.</p>

          {comparison !== null && (
            <section aria-label="Сравнение с прошлым прогоном">
              <header className="platform-resources__toolbar">
                <h3>Сравнение с прошлым прогоном</h3>
                <Button
                  variant="ghost"
                  icon={<History size={16} aria-hidden="true" />}
                  onClick={() => actions.openRun(comparison.previousRunId)}
                  actionId="platform.testing-run.open-previous"
                >
                  Открыть прошлый
                </Button>
              </header>
              {comparison.note !== null && <p className="platform-testing__note">{comparison.note}</p>}
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Метрика слияния</th>
                      <th>Было</th>
                      <th>Стало</th>
                      <th>Разница</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.rows.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{row.previous}</td>
                        <td>{row.current}</td>
                        <td>{row.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {vm.lanes.map((lane) => (
            <section key={lane.lane} aria-label={lane.title}>
              <h3>
                {lane.title} · кадров {lane.frames}
              </h3>
              <dl className="platform-testing__scores">
                {lane.cells.map((cell) => (
                  <div key={cell.label}>
                    <dt>{cell.label}</dt>
                    <dd>
                      <Share share={cell.share} />
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}

          {vm.products.length > 0 && (
            <section aria-label="Товары, худшие первыми">
              <h3>Товары — худшие первыми</h3>
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Товар</th>
                      <th>Первое место</th>
                      <th>В пятёрке</th>
                      <th>Очко места</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.products.map((product) => (
                      <tr key={product.productId}>
                        <td>
                          <Button
                            variant="ghost"
                            icon={<FolderOpen size={16} aria-hidden="true" />}
                            onClick={() => actions.openProduct(product.productId)}
                            actionId="platform.testing-run.open-product"
                          >
                            {product.name}
                          </Button>
                          <div className="platform-testing__note">кадров {product.frames}</div>
                        </td>
                        <td>
                          <Share share={product.top1} />
                        </td>
                        <td>
                          <Share share={product.top5} />
                        </td>
                        <td>
                          <Share share={product.position} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {vm.tags.length > 0 && (
            <section aria-label="Срезы по пометкам кадров">
              <h3>По пометкам кадров</h3>
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Пометка</th>
                      <th>Первое место</th>
                      <th>В пятёрке</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.tags.map((tag) => (
                      <tr key={tag.key}>
                        <td>
                          {tag.axis}: {tag.value}
                          <div className="platform-testing__note">кадров {tag.frames}</div>
                        </td>
                        <td>
                          <Share share={tag.top1} />
                        </td>
                        <td>
                          <Share share={tag.top5} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {vm.confusions.length > 0 && (
            <section aria-label="Путаницы">
              <h3>С чем путается</h3>
              <ul>
                {vm.confusions.map((confusion) => (
                  <li key={confusion.key}>
                    {confusion.text} — {confusion.count} раз
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-label="Время и условия">
            <h3>Время и условия</h3>
            <dl>
              <dt>Время ответа</dt>
              <dd>{vm.latency ?? 'нет данных'}</dd>
              <dt>Кадры со сбоем</dt>
              <dd>{vm.errors === 0 ? 'нет' : `${vm.errors} — в баллы не входят`}</dd>
              {vm.stamp.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </section>
      )}
    </TestingGate>
  )
}
